import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateAfterCompanyMutation } from 'core/api/invalidation';
import { humanizeApiError, mapFieldErrors, firstFieldError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { companyApi } from 'features/company/company.api';
import { Card, HeroBand } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { TextInput } from 'shared/components/Input';
import { Button } from 'shared/components/Button';

const INITIAL = {
  legal_name: '',
  display_name: '',
  registration_number: '',
  contact_email: '',
  contact_phone: '',
  contact_address: '',
};

export function CompanyOnboardingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  const legalNameId = useId();
  const displayNameId = useId();
  const registrationId = useId();
  const emailId = useId();
  const phoneId = useId();
  const addressId = useId();

  const update = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setMessage('');
    try {
      await companyApi.createCompany(form);
      await invalidateAfterCompanyMutation(queryClient);
      navigate(paths.companyPortal, { replace: true });
    } catch (error) {
      setErrors(mapFieldErrors(error.errors || {}));
      setMessage(humanizeApiError(error, t));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="company-onboarding-page">
      <PageMeta titleKey="meta.companyOnboarding" />
      <HeroBand
        kicker={t('company.onboarding.kicker')}
        title={t('company.onboarding.title')}
        body={t('company.onboarding.body')}
      />
      <Card as="form" onSubmit={(event) => { void handleSubmit(event); }}>
        <TextInput
          id={legalNameId}
          label={t('company.onboarding.legalName')}
          value={form.legal_name}
          onChange={update('legal_name')}
          error={firstFieldError(errors, 'legal_name')}
          required
        />
        <TextInput
          id={displayNameId}
          label={t('company.onboarding.displayName')}
          hint={t('company.onboarding.displayNameHint')}
          value={form.display_name}
          onChange={update('display_name')}
          error={firstFieldError(errors, 'display_name')}
        />
        <TextInput
          id={registrationId}
          label={t('company.onboarding.registrationNumber')}
          value={form.registration_number}
          onChange={update('registration_number')}
          error={firstFieldError(errors, 'registration_number')}
        />
        <TextInput
          id={emailId}
          label={t('company.onboarding.contactEmail')}
          type="email"
          value={form.contact_email}
          onChange={update('contact_email')}
          error={firstFieldError(errors, 'contact_email')}
        />
        <TextInput
          id={phoneId}
          label={t('company.onboarding.contactPhone')}
          value={form.contact_phone}
          onChange={update('contact_phone')}
          error={firstFieldError(errors, 'contact_phone')}
        />
        <TextInput
          id={addressId}
          label={t('company.onboarding.contactAddress')}
          value={form.contact_address}
          onChange={update('contact_address')}
          error={firstFieldError(errors, 'contact_address')}
        />
        {message ? <p className="field-error" role="alert">{message}</p> : null}
        <Button type="submit" loading={submitting}>{t('company.onboarding.submit')}</Button>
      </Card>
    </div>
  );
}
